import { field, text, date, writer, relation, children, json } from '@nozbe/watermelondb/decorators';
import { Model } from '@nozbe/watermelondb';
import { sanitizeAttachments } from '../../constants';
import { CASH_TXN_SCHEMA } from '../schema';

export default class CashTxn extends Model {
    static table = CASH_TXN_SCHEMA;

    static associations = {
        payment: { type: 'has_many', foreignKey: 'cash_txn_id' },
        customer: { type: 'belongs_to', key: 'customer_id' },
        business: { type: 'belongs_to', key: 'business_id' },
    }

    @text('invoice_number') invoice_number;
    @text('invoice_prefix') invoice_prefix;
    @text('wallet_txns_id') wallet_txns_id;
    @text('invoice_title') invoice_title;
    @text('discount_unit') discount_unit;
    @text('document_url') document_url;
    @text('image_url') image_url;
    @text('recording') recording;
    @text('signature') signature;
    @text('tax_title') tax_title;
    @text('tranType') tranType;
    @text('category') category;
    @text('status') status;
    @text('method') method;
    @text('wallet') wallet;
    @text('note') note;
    @text('type') type;
    @text('bank') bank;

    @field('advance_amount') advance_amount;
    @field('sales_amount') sales_amount;
    @field('cost_amount') cost_amount;
    @field('sub_total') sub_total;
    @field('is_synced') is_synced;
    @field('discount') discount;
    @field('tax_rate') tax_rate;
    @field('profit') profit;
    @field('amount') amount;
    @field('total') total;

    @date('transacted_at') transacted_at;
    @date('created_at') created_at;

    @json('attachment', sanitizeAttachments) attachment;

    @relation('customer', 'customer_id') customer;
    @relation('business', 'business_id') business;

    @children('payment') payment;

    @writer async maskAsPaid() {
        await this.update(cashTxn => {
            cashTxn.transacted_at = new Date();
            cashTxn.status = 'paid';
        });
    };

    @writer async updateCashTxnImage(url) {
        await this.update(cashTxn => {
            cashTxn.image_url = url;
        });
    };

    @writer async updateCashTxnRecording(url) {
        await this.update(cashTxn => {
            cashTxn.recording = url;
        });
    };

    @writer async updateCashTxnSignature(url) {
        await this.update(cashTxn => {
            cashTxn.signature = url;
        });
    };

    @writer async updateCashTxnDocument(url) {
        await this.update(cashTxn => {
            cashTxn.document_url = url;
        });
    };

    @writer async updateCashTxnPaymentMethod(details) {
        await this.update(cashTxn => {
            cashTxn.method = details.method;
            cashTxn.wallet = details.wallet;
            cashTxn.bank = details.bank;
        });
    };

    @writer async updateCashTxn(details) {
        return await this.update(cashTxn => {
            cashTxn.customer.set(details.customer || null);
            cashTxn.recording = details?.recording_uri || null;
            cashTxn.advance_amount = details.advance_amount;
            cashTxn.invoice_number = details.invoice_number;
            cashTxn.invoice_prefix = details.invoice_prefix;
            cashTxn.invoice_title = details.invoice_title;
            cashTxn.discount_unit = details.discount_unit;
            cashTxn.sales_amount = details.sales_amount;
            cashTxn.document_url = details.document_url;
            cashTxn.cost_amount = details.cost_amount;
            cashTxn.attachment = details.attachment;
            cashTxn.sub_total = details.sub_total;
            cashTxn.signature = details.signature;
            cashTxn.tax_title = details.tax_title;
            cashTxn.image_url = details.image_url;
            cashTxn.transacted_at = details.date;
            cashTxn.category = details.category;
            cashTxn.discount = details.discount;
            cashTxn.tax_rate = details.tax_rate;
            cashTxn.amount = details.amount;
            cashTxn.method = details.method;
            cashTxn.wallet = details.wallet;
            cashTxn.profit = details.profit;
            cashTxn.status = details.status;
            cashTxn.total = details.total;
            cashTxn.note = details.note;
            cashTxn.bank = details.bank;
        });
    };

    // Alias expected by the generic contact-picker screens (PhoneBook/ContactList,
    // PhoneBook/ListComponent, CreateCustomerForm), which call this method name
    // unconditionally on whatever transaction record they're passed.
    @writer async updateInvoiceCustomer(customer) {
        await this.update(cashTxn => {
            cashTxn.customer.set(customer);
        });
    };

    @writer async updateCashTxnStatusAmount(details) {
        return await this.update(cashTxn => {
            cashTxn.wallet_txns_id = details?.wallet_txns_id || '';
            cashTxn.method = details?.method || cashTxn.method;
            cashTxn.wallet = details?.wallet || cashTxn.wallet;
            cashTxn.advance_amount = details.advance_amount;
            cashTxn.bank = details?.bank || cashTxn.bank;
            cashTxn.sales_amount = details.sales_amount;
            cashTxn.cost_amount = details.cost_amount;
            cashTxn.amount = details.amount;
            cashTxn.status = details.status;
        });
    };

    @writer async updateCashTxnAttachments(attachment) {
        await this.update(cashTxn => {
            cashTxn.attachment = attachment;
        });
    };

    @writer async updateTxnsPaidByOnline(details) {
        return await this.update(cashTxn => {
            details?.customer?.id && cashTxn.customer.set(details.customer);
            cashTxn.category = details?.category || cashTxn.category;
            cashTxn.wallet_txns_id = details?.wallet_txns_id || '';
            cashTxn.method = details?.method || cashTxn.method;
            cashTxn.wallet = details?.wallet || cashTxn.wallet;
            cashTxn.bank = details?.bank || cashTxn.bank;
            cashTxn.sales_amount = details.sales_amount;
            cashTxn.cost_amount = details.cost_amount;
            cashTxn.amount = details.amount;
            cashTxn.status = details.status;
            cashTxn.note = details.note;
        });
    };

    @writer async updateCashTxnCustomer(customer) {
        await this.update(cashTxn => {
            cashTxn.customer.set(customer);
        });
    };

    @writer async updateCashTxnCategory(category) {
        await this.update(cashTxn => {
            cashTxn.category = category;
        });
    };

    @writer async markAsDeleted() {
        await this.payment.destroyAllPermanently();
        await super.markAsDeleted();
    };
};

